<template>
    <div>
        <div class="backButton">
            <button @click="goToList"><i class="fas fa-arrow-left-long"></i> Back</button>
        </div>
        <div style="display: flex; flex-direction: row; justify-content: center;">
            <div class="accordion-info-block">
                <h4>Name: <input type="text" v-model="accordion.name" @blur="saveAccordion('name')" :disabled="!accordion.isActive" /></h4>
                <h4>Code: <input type="text" v-model="accordion.code" @blur="saveAccordion('code')" :disabled="!accordion.isActive" /></h4>
            </div>
            <div class="accordion-info-block" style="width: 300px; align-items: flex-start; margin: 5px 0 0 50px;">
                <h4>Status: 
                    <span v-if="accordion.isActive">Active</span>
                    <span v-if="!accordion.isActive">Inactive</span>
                </h4>
                <LanguageManagement v-if="accordion.languages" :objectLanguageArray="accordion.languages" :defaultLanguage="accordion.defaultLanguage"
                        :onLanguageAdd="addLanguage" :onDefaultChange="changeDefaultLanguage" :onLanguageDelete="deleteLanguage"></LanguageManagement>
            </div>
        </div>
        <div class="accordionButtons">
            <div class="flexButtonContainer">
                <button class="testButton" @click="copyAccordion">Copy Accordion</button>
                <button class="reactivateButton" v-if="!accordion.isActive" @click="reactivateAccordion">Reactivate Accordion</button>
                <button class="deactivateButton" v-if="accordion.isActive" @click="deactivateAccordion">Deactivate Accordion</button>
                <ItemModal v-if="accordion.testConfigurationId === 0"
                            :formRows="testConfigurationFormRows"
                            :saveItemFunction="saveTestConfiguration"
                            :isActive="accordion.isActive"
                            itemName="Test Configuration"
                            :openModalButton="openTestConfigurationButtonHtml"></ItemModal>
                <button v-if="accordion.testConfigurationId > 0" 
                        class="testButton" 
                        @click="goToTestConfiguration">
                        Open Test Configuration</button>
            </div>
            <p>Show deleted elements <input type="checkbox" v-model="showDeletedElements" @change="getAccordion" /></p>
        </div>
        <h3>Headers</h3>
        <div v-for="header in activeItems(accordion.headers)" :key="header.id" class="sectionContainer" style="position:relative; flex-wrap:nowrap;">
            <button v-if="header.isActive" class="deleteButton ButtonAbsolute" @click="deleteAccordionExtraText(accordion.id, header.id, 'header')" :disabled="!accordion.isActive || !header.isActive">
                <i class="fas fa-trash-can"></i>
            </button>
            <button v-if="!header.isActive" class="reactivateButton ButtonAbsolute" @click="reactivateAccordionExtraText(accordion.id, header)" :disabled="!accordion.isActive">
                Reactivate
            </button>
            <div class="flexColumn">
                <div class="detailSequence">
                    <p><b>Sequence</b></p>
                    <input type="number" v-model="header.sequence" @blur="saveAccordionExtraText(header, 'sequence')" :disabled="!accordion.isActive || !header.isActive"/>
                </div>
                <div class="detailSequence">
                    <p><b>CSS Field Name</b></p>
                    <AutoComplete :data="cssFieldElementsExtraText" 
                                    :value="header.cssClass" 
                                    :onValueChanged="updateCssFieldExtraText"
                                    :onBlur="onCssFieldAutoCompleteBlurExtraText"
                                    :passThruObject="header"
                                    internalValueField="searchValue"
                                    displayField="displayValue"
                                    searchField="searchValue"></AutoComplete>
                </div>
            </div>
            <div class="detailDisplayText">
                <DisplayText :displayText="header.displayText" :object="header" :onChanged="saveAccordionExtraText_fromComponent" :disabled="!accordion.isActive || !header.isActive"></DisplayText>
                <TranslatedText v-if="header.displayText && accordion.languages && accordion.languages.length > 1"
                    :textJson="header.displayTextJson" :defaultLanguage="accordion.defaultLanguage"></TranslatedText>
            </div>
            <div class="detailMediaProperty" >
                <b class="detailHeader">Image</b>
                <button @click="showAddMediaPropertyModal(header)" class="openModalButton" :disabled="!accordion.isActive">
                    <i v-if="!header.mediaProperty" class="fa-circle-plus fas fa-lg"></i>
                    <i v-if="header.mediaProperty" class="fa-pen-to-square fas fa-lg"></i>
                </button>
                <button v-if="header.mediaProperty" @click="removeMediaPropertyFromExtraText(header)" class="removeMediaPropertyButton" :disabled="!accordion.isActive">
                    <i class="fa-trash-can fas fa-lg"></i>
                </button>
                <AddMediaPropertyModal v-if="header.showAddMediaPropertyModal" 
                                        @hideAddMediaPropertyModal="hideAddMediaPropertyModal" 
                                        :object="header" 
                                        :isActive="accordion.isActive" 
                                        :mediaTypes="mediaTypes" 
                                        @saveMediaProperty="saveMediaPropertyToExtraText" 
                                        mediaTypeFilter="Image"></AddMediaPropertyModal>
                <div v-if="header.mediaProperty">
                    <p><b>Type:</b> {{ header.mediaProperty.mediaType }}</p>
                    <p><b>Description:</b> {{ header.mediaProperty.description }}</p>
                </div>
            </div>
            <div class="detailConditionalJson" style="display:flex; flex-direction: column;margin-right: 50px;">
                <div class="detailHeader" style="font-weight: bold; width: unset; display: flex; align-items: baseline;flex-direction: row;">
                    Display Conditions:<i @click="showConditionalModal(header, 'extraText')" class="fa-pen-to-square fas fa-lg editFieldButton"></i>
                </div>
                <div class="conditionalJsonText" v-html="getPrintConditions( header.conditionalJson )"></div>
            </div>
        </div>
        <ItemModal :itemName="'Header'"
                        :formRows="generalFormRows(true)"
                        :saveItemFunction="saveNewAccordionExtraTextHeader"
                        :refreshItemsFunction="getAccordion"
                        :isActive="accordion.isActive"></ItemModal>
        <div>
            <h3>Sections</h3>
            <div v-if="accordion && accordion.sections && accordion.sections.length" class="showHideAll">
                <h4 v-if="!allSectionsExpanded" @click="toggleAllSections()"><i class="fas fa-circle-plus"></i> Show All Details</h4>
                <h4 v-if="allSectionsExpanded" @click="toggleAllSections()"><i class="fas fa-circle-minus"></i> Hide All Details</h4>
            </div>
        </div>
        <div v-for="section in activeItems(accordion.sections)" :key="section.id" class="sectionContainer">
            <div class="tabsContainer" v-if="!section.linkedAccordionSectionID">
                <div v-for="(tab, index) in tabs" @click="section.currentTab = index" class="tab" :class="{active: section.currentTab === index}" :key="tab">{{tab}}</div>
            </div>
            <div class="sectionMinimized">
                <div class="section">
                    <button v-if="section.isActive" class="deleteButton" @click="deleteAccordionSection(accordion.id, section.id)" :disabled="!accordion.isActive">
                        <i class="fas fa-trash-can"></i>
                    </button>
                    <button v-if="!section.isActive" class="reactivateButton" @click="reactivateAccordionSection(accordion.id, section)" :disabled="!accordion.isActive">
                        Reactivate
                    </button>
                    <div class="sectionTitle">
                        <h3>Section</h3>
                    </div>
                    <div class="sectionSequence">
                        <p><b>Sequence</b></p>
                        <input type="number" v-model="section.sequence" @blur="saveAccordionSection(section, 'sequence')" :disabled="!accordion.isActive || !section.isActive"/>
                    </div>
                    <div class="detailSequence">
                        <p><b>Layout</b></p>
                        <select v-model="section.layout" @blur="saveAccordionSection(section, 'layout')">
                            <option v-for="layout in layouts" :key="layout.value" :value="layout.internalName">{{layout.internalName != 'NotSet' ? layout.internalName : ''}}</option>
                        </select>
                    </div>
                    <div class="detailConditionalJson" style="display:flex; flex-direction: column;margin-right: 50px;">
                        <div class="detailHeader" style="font-weight: bold; width: unset; display: flex; align-items: baseline;flex-direction: row;">
                            Display Conditions:<i @click="showConditionalModal(section, 'section')" class="fa-pen-to-square fas fa-lg editFieldButton"></i>
                        </div>
                        <div class="conditionalJsonText" v-html="getPrintConditions( section.conditionalJson )"></div>
                    </div>
                    <div v-if="section.currentTab === 0 && !section.linkedAccordionSectionID" class="showHideDetails">
                        <span v-if="!expandedSections[section.id]" @click="toggleOpen(section)"><i class="fas fa-circle-plus"></i> Show Details</span>
                        <span v-if="expandedSections[section.id]" @click="toggleOpen(section)"><i class="fas fa-circle-minus"></i> Hide Details</span>
                    </div>
                </div>
                <template v-if="section.currentTab === 0 && !section.linkedAccordionSectionID">
                    <div class="sectionHeader" v-for="header in activeItems(section.headers)" :key="header.id">
                        <button v-if="header.isActive" class="deleteButton" @click="deleteAccordionHeader(accordion.id, section.id, header.id)" :disabled="!accordion.isActive || !section.isActive">
                            <i class="fas fa-trash-can"></i>
                        </button>
                        <button v-if="!header.isActive" class="reactivateButton" @click="reactivateAccordionHeader(accordion.id, section.id, header)" :disabled="!accordion.isActive || !section.isActive">
                            Reactivate
                        </button>
                        <h4>Header</h4>
                        <div class="headerFieldContainer">
                            <div class="headerTextFields">
                                <div class="headerSequence">
                                    <p><b>Sequence</b></p>
                                    <input type="number" v-model="header.sequence" @blur="saveAccordionHeader(section.id, header, 'sequence')" :disabled="!accordion.isActive || !header.isActive || !section.isActive" />
                                </div>
                                <div class="headerMininmizedText">
                                    <p>
                                        <ToolTip position="right" alignment="end" >
                                            <template #hoverText>
                                                <b style="margin-right: 10px">Minimized text</b>
                                            </template>
                                            <template #content>
                                                <p>For the AccordionSectionView display component with side navigation menu, the Expanded Text is used for the header in the 
                                                    Accordion display and the Minimized Text is used for the nav menu (leave it blank to keep it off the menu)</p>
                                            </template>
                                        </ToolTip>
                                        <TranslatedText v-if="header.minimizedText && accordion.languages && accordion.languages.length > 1"
                                            :textJson="header.minimizedTextJson" alignment="start" :defaultLanguage="accordion.defaultLanguage"></TranslatedText>
                                    </p>
                                    <input type="text" v-model="header.minimizedText" @blur="saveAccordionHeader(section.id, header, 'minimizedText')" :disabled="!accordion.isActive || !header.isActive || !section.isActive" />
                                </div>
                                <div class="headerExpandedText">
                                    <p>
                                        <b style="margin-right: 10px">Expanded text</b>
                                        <TranslatedText v-if="header.expandedText && accordion.languages && accordion.languages.length > 1"
                                            :textJson="header.expandedTextJson" :defaultLanguage="accordion.defaultLanguage"></TranslatedText>
                                    </p>
                                    <input type="text" v-model="header.expandedText" @blur="saveAccordionHeader(section.id, header, 'expandedText')" :disabled="!accordion.isActive || !header.isActive || !section.isActive" />
                                </div>
                            </div>
                            <div class="detailConditionalJson" style="display:flex; flex-direction: column;margin-right: 50px;">
                                <div class="detailHeader" style="font-weight: bold; width: unset; display: flex; align-items: baseline;flex-direction: row;">
                                    Display Conditions:<i @click="showConditionalModal(header, 'header')" class="fa-pen-to-square fas fa-lg editFieldButton"></i>
                                </div>
                                <div class="conditionalJsonText" v-html="getPrintConditions( header.conditionalJson )"></div>
                            </div>
                        </div>
                    </div>
                    <ItemModal :itemName="'Header'"
                                    :formRows="newHeaderFormRows"
                                    :saveItemFunction="saveNewAccordionHeader"
                                    :refreshItemsFunction="getAccordion"
                                    :parentId="section.id"
                                    :isActive="accordion.isActive"></ItemModal>
                </template>
                <template v-if="section.currentTab === 1 && !section.linkedAccordionSectionID">
                    <div style="margin-left:35px;">
                        <AudioListing :parentID="section.id" :grandParentID="accordion.id" :actions="sectionActions(section.id)" :allowEdit="accordion.isActive"
                            :onUpdate="addAction" objectType="AccordionAction"></AudioListing>
                    </div>
                </template>
                <template v-if="section.linkedAccordionSectionID">
                    <div style="padding-left: 40px;">
                        This section is tied to another section.   It is done to reduce duplication of data entry.   <br /> The only thing that is not copied are the section 
                        level values to the left.<br />
                        Ideally, a different set of section level display conditions control whether or not this is displayed.<br />
                        <b>Linked Section:</b> <select v-model="section.linkedAccordionSectionID"  @blur="saveAccordionSection(section, 'linkedAccordionSectionID')">
                            <option v-for="opt in SectionsThatCanBeLinked" :key="opt.internalName" :value="opt.internalName">{{opt.displayName}}</option>
                        </select>
                    </div>
                </template>
            </div>
            <div class="sectionExpanded" v-if="expandedSections[section.id] && section.currentTab === 0">
                <hr/>
                <span class="subtitle">Details</span>
                <ItemModal :itemName="'Detail'"
                                :formRows="generalFormRows(false)"
                                :saveItemFunction="addAccordionDetail"
                                :parentId="section.id"
                                :isActive="accordion.isActive"></ItemModal>
                <div v-for="detail in activeItems(section.details)" :key="detail.id" class="detail">
                    <button v-if="detail.isActive" class="deleteButton" @click="deleteAccordionDetail(accordion.id, section.id, detail.id)" :disabled="!accordion.isActive || !section.isActive">
                        <i class="fas fa-trash-can"></i>
                    </button>
                    <button v-if="!detail.isActive" class="reactivateButton" @click="reactivateAccordionDetail(accordion.id, section.id, detail)" :disabled="!accordion.isActive || !section.isActive">
                        Reactivate
                    </button>
                    <div style="display: flex; flex-direction: column;">
                        <div style="display: flex; flex-direction: row; align-items:center;">
                            <p class="detailHeader">Sequence</p>
                            <input type="number" v-model="detail.sequence" @blur="saveAccordionDetail(section.id, detail, 'sequence')" :disabled="!accordion.isActive || !detail.isActive || !section.isActive"/>
                        </div>
                        <div style="display: flex; flex-direction: row; align-items:center;">
                            <p><b>CSS Class</b></p>
                            <Multiselect @select="saveAccordionDetail(section.id, detail, 'cssClass')"
                                            @remove="saveAccordionDetail(section.id, detail, 'cssClass')"
                                            v-model="detail.cssClass"
                                            :options="cssFieldElementsDetail"
                                            placeholder="Type to search"
                                            open-direction="bottom"
                                            :multiple="true"
                                            :searchable="true"
                                            :taggable="true" 
                                            @tag="(newClass) => {addCssClass(newClass, detail, section.id)}"></Multiselect>
                        </div>
                    </div>
                    <div class="detailMediaProperty" >
                        <b class="detailHeader">Media Property</b>
                        <button v-if="!isSharedDetail(detail)" @click="showAddMediaPropertyModal(detail)" class="openModalButton" :disabled="!accordion.isActive">
                            <i v-if="!detail.mediaProperty" class="fa-circle-plus fas fa-lg"></i>
                            <i v-if="detail.mediaProperty" class="fa-pen-to-square fas fa-lg"></i>
                        </button>
                        <button v-if="detail.mediaProperty && !isSharedDetail(detail)" @click="removeMediaPropertyFromDetail(detail)" class="removeMediaPropertyButton" :disabled="!accordion.isActive">
                            <i class="fa-trash-can fas fa-lg"></i>
                        </button>
                        <AddMediaPropertyModal v-if="detail.showAddMediaPropertyModal" @hideAddMediaPropertyModal="hideAddMediaPropertyModal" 
                            :object="detail" :isActive="accordion.isActive" :mediaTypes="mediaTypes" @saveMediaProperty="saveMediaPropertyToDetail"></AddMediaPropertyModal>
                        <div v-if="detail.mediaProperty">
                            <p><b>Type:</b> {{ detail.mediaProperty.mediaType }}</p>
                            <p><b>Description:</b> {{ detail.mediaProperty.description }}</p>
                        </div>
                    </div>
                    <div class="detailConditionalJson" style="display:flex; flex-direction: column;margin-right: 50px;">
                        <div class="detailHeader" style="font-weight: bold; width: unset; display: flex; align-items: baseline;flex-direction: row;">
                            Display Conditions:<i @click="showConditionalModal(detail, 'detail')" class="fa-pen-to-square fas fa-lg editFieldButton"></i>
                        </div>
                        <div class="conditionalJsonText" v-html="getPrintConditions( detail.conditionalJson )"></div>
                    </div>
                    <div class="detailDisplayText">
                        <p v-if="!detail.showDisplayText"><b>Display Text</b> <i v-if="!isSharedDetail(detail)" @click="detail.showDisplayText = true" class="fa-pen-to-square fas fa-lg editFieldButton"></i></p> 
                        <span v-if="!detail.showDisplayText" v-html="getDetailDisplayText(detail)"></span>
                        <div class="flexBreak"></div>
                        <TranslatedText v-if="detail.displayText && accordion.languages && accordion.languages.length > 1"
                            :textJson="detail.displayTextJson" :defaultLanguage="accordion.defaultLanguage"></TranslatedText>
                        <DisplayText v-if="detail.showDisplayText && !isSharedDetail(detail)" :displayText="detail.displayText" :object="detail" :onChanged="saveAccordionDetail_fromComponent" :disabled="!accordion.isActive || !detail.isActive || !section.isActive"></DisplayText>
                        <div class="notSaved" v-if="!detail.id">NOT SAVED! Please complete required fields.</div>
                    </div>
                    <div v-if="detail.sharedDetailID === 0" style="display: flex; flex-direction: column; align-items: flex-start;">
                        <ItemModal v-if="detail.id"
                                    :itemName="'Shared Detail'"
                                    :formRows="sharedDetailFormRows"
                                    :saveItemFunction="saveNewSharedDetail"
                                    :refreshItemsFunction="getAccordion"
                                    :isActive="accordion.isActive"
                                    :passThruObject="detail"
                                    openModalButton="<button class=&quot;sharedDetailButton&quot;>Convert to Shared Detail</button>"></ItemModal>
                        <InsertSharedAccordionDetail :saveAccordionDetail="saveSharedDetail"
                                                    :accordionDetail="detail"
                                                    :isActive="accordion.isActive"
                                                    :tags="tags"></InsertSharedAccordionDetail>
                    </div>
                    <div v-if="detail.sharedDetailID > 0" style="display: flex; flex-direction: column; align-items: flex-start;">
                        <p>This detail is shared across other accordions</p>
                        <button class="sharedDetailButton" @click="saveAsUniqueDetail(detail)">Convert to unique Detail</button>
                        <InsertSharedAccordionDetail :saveAccordionDetail="saveSharedDetail"
                                                    :accordionDetail="detail"
                                                    :isActive="accordion.isActive"
                                                    :tags="tags"></InsertSharedAccordionDetail>
                    </div>
                </div>
                <ItemModal v-if="section.details.length > 0"
                                :itemName="'Detail'"
                                :formRows="generalFormRows(false)"
                                :saveItemFunction="addAccordionDetail"
                                :parentId="section.id"
                                :isActive="accordion.isActive"></ItemModal>
            </div>
        </div>
        <ItemModal :itemName="'Section'"
                        :formRows="newSectionFormRows"
                        :saveItemFunction="saveNewAccordionSection"
                        :refreshItemsFunction="getAccordion"
                        :isActive="accordion.isActive"></ItemModal>
        <h3>Footers</h3>
        <div v-for="footer in activeItems(accordion.footers)" :key="footer.id" class="sectionContainer" style="position:relative; flex-wrap:nowrap;">
            <button v-if="footer.isActive" class="deleteButton ButtonAbsolute" @click="deleteAccordionExtraText(accordion.id,footer.id, 'footer')" :disabled="!accordion.isActive || !footer.isActive">
                <i class="fas fa-trash-can"></i>
            </button>
            <button v-if="!footer.isActive" class="reactivateButton ButtonAbsolute" @click="reactivateAccordionExtraText(accordion.id, footer)" :disabled="!accordion.isActive">
                Reactivate
            </button>
            <div class="flexColumn">
                <div class="detailSequence">
                    <p><b>Sequence</b></p>
                    <input type="number" v-model="footer.sequence" @blur="saveAccordionExtraText(footer, 'sequence')" :disabled="!accordion.isActive || !footer.isActive"/>
                </div>
                <div class="detailSequence">
                    <p><b>CSS Field Name</b></p>
                    <AutoComplete :data="cssFieldElementsExtraText" 
                                    :value="footer.cssClass" 
                                    :onValueChanged="updateCssFieldExtraText"
                                    :onBlur="onCssFieldAutoCompleteBlurExtraText"
                                    :passThruObject="footer"
                                    internalValueField="searchValue"
                                    displayField="displayValue"
                                    searchField="searchValue"></AutoComplete>
                </div>
            </div>
            <div class="detailDisplayText">
                <DisplayText :displayText="footer.displayText" :object="footer" :onChanged="saveAccordionExtraText_fromComponent" :disabled="!accordion.isActive || !footer.isActive"></DisplayText>
                <TranslatedText v-if="footer.displayText && accordion.languages && accordion.languages.length > 1"
                    :textJson="footer.displayTextJson" :defaultLanguage="accordion.defaultLanguage"></TranslatedText>
            </div>
            <div class="detailMediaProperty" >
                <b class="detailHeader">Image</b>
                <button @click="showAddMediaPropertyModal(footer)" class="openModalButton" :disabled="!accordion.isActive">
                    <i v-if="!footer.mediaProperty" class="fa-circle-plus fas fa-lg"></i>
                    <i v-if="footer.mediaProperty" class="fa-pen-to-square fas fa-lg"></i>
                </button>
                <button v-if="footer.mediaProperty" @click="removeMediaPropertyFromExtraText(footer)" class="removeMediaPropertyButton" :disabled="!accordion.isActive">
                    <i class="fa-trash-can fas fa-lg"></i>
                </button>
                <AddMediaPropertyModal v-if="footer.showAddMediaPropertyModal" 
                                        @hideAddMediaPropertyModal="hideAddMediaPropertyModal" 
                                        :object="footer" 
                                        :isActive="accordion.isActive" 
                                        :mediaTypes="mediaTypes" 
                                        @saveMediaProperty="saveMediaPropertyToExtraText" 
                                        mediaTypeFilter="Image"></AddMediaPropertyModal>
                <div v-if="footer.mediaProperty">
                    <p><b>Type:</b> {{ footer.mediaProperty.mediaType }}</p>
                    <p><b>Description:</b> {{ footer.mediaProperty.description }}</p>
                </div>
            </div>
            <div class="detailConditionalJson" style="display:flex; flex-direction: column;margin-right: 50px;">
                <div class="detailHeader" style="font-weight: bold; width: unset; display: flex; align-items: baseline;flex-direction: row;">
                    Display Conditions:<i @click="showConditionalModal(footer, 'extraText')" class="fa-pen-to-square fas fa-lg editFieldButton"></i>
                </div>
                <div class="conditionalJsonText" v-html="getPrintConditions( footer.conditionalJson )"></div>
            </div>
        </div>
        <ItemModal :itemName="'Footer'"
                        :formRows="generalFormRows(true)"
                        :saveItemFunction="saveNewAccordionExtraTextFooter"
                        :refreshItemsFunction="getAccordion"
                        :isActive="accordion.isActive"></ItemModal>
        <h3>Actions</h3>
        <div v-if="accordion.id" class="sectionContainer">
            <AudioListing :grandParentID="accordion.id" :actions="sectionActions(0)" :allowEdit="accordion.isActive"
                        :onUpdate="addAction" objectType="AccordionAction"></AudioListing>
        </div>
        <ConditionalExpressionModal :conditions="JSON.parse(conditionalExpressionObject.conditionalJson)" v-if="ExpressionModalVisible" @close="ExpressionModalVisible=false" 
            :onConditionsChanged="updateConditions"></ConditionalExpressionModal>
    </div>
</template>

<script>
import {data} from '../../shared/data';
import {sorting} from '../../shared/sorting';
import ItemModal from '@/components/ItemModal.vue';
import AddMediaPropertyModal from '@/components/mediaproperty/AddMediaPropertyModal.vue';
import DisplayText from '@/components/shared/DisplayText.vue';
import InsertSharedAccordionDetail from '@/components/sharedAccordionDetail/InsertSharedAccordionDetail.vue';
import ConditionalExpressionModal from '@/components/shared/ConditionalExpressions/ConditionalExpressionModal.vue';
import { printConditions } from '@/shared/logicProcessing';
import AutoComplete from '@/components/shared/AutoComplete.vue';
import Multiselect from 'vue-multiselect';
import AudioListing from '@/components/mediaproperty/AudioListing.vue';
import {translation} from '../../shared/translation';
import TranslatedText from '@/components/shared/TranslatedText.vue';
import LanguageManagement from '@/components/shared/LanguageManagement.vue';
import ToolTip from '@/components/shared/ToolTip2.vue';

export default {
    name: 'AccordionEdit',
    components: { ItemModal, DisplayText, AddMediaPropertyModal, InsertSharedAccordionDetail, LanguageManagement,
                    ConditionalExpressionModal, AutoComplete, Multiselect, AudioListing, TranslatedText, ToolTip },
    data: function() {
        return {
            accordion: {},
            mediaProperties: [],
            mediaTypes: [],
            layouts: [],
            tags: [],
            surveys: [],
            showDeletedElements: false,
            tabs: ["Content", "Actions"],
            openTestConfigurationButtonHtml: '<button class="testButton">Create Test Configuration</button>',
            cssFieldElementsExtraText: [
                {
                    displayValue: 'h1',
                    searchValue: 'h1'
                },
                {
                    displayValue: 'h2',
                    searchValue: 'h2'
                },
                {
                    displayValue: 'h3',
                    searchValue: 'h3'
                },
                {
                    displayValue: 'h4',
                    searchValue: 'h4'
                },
                {
                    displayValue: 'h5',
                    searchValue: 'h5'
                },
                {
                    displayValue: 'h6',
                    searchValue: 'h6'
                },
            ],
            cssFieldElementsDetail: [
                'h1',
                'h2',
                'h3',
                'h4',
                'h5',
                'h6',
                'box-inner',
                'blue'
            ],
            testConfigurationFormRows: [
                {
                    name: 'Survey',
                    propertyName: 'surveyID',
                    required: false,
                    element: 'select',
                    selectOptions: []
                }
            ],
            newSectionFormRows: [
                {
                    name: 'Sequence',
                    propertyName: 'sequence',
                    required: true,
                    element: 'input'
                },
                {
                    name: 'Linked Section',
                    propertyName: 'linkedAccordionSectionID',
                    required: false,
                    element: 'select',
                    selectOptions: [] 
                }
            ],
            newHeaderFormRows: [
                {
                    name: 'Sequence',
                    propertyName: 'sequence',
                    required: true,
                    element: 'input'
                },
                {
                    name: 'Minimized text',
                    propertyName: 'minimizedText',
                    element: 'input'
                },
                {
                    name: 'Expanded text',
                    propertyName: 'expandedText',
                    element: 'input'
                }
            ],
            sharedDetailFormRows: [
                {
                    name: 'Description',
                    propertyName: 'description',
                    required: true,
                    element: 'textarea'
                },
                {
                    name: 'Tags',
                    propertyName: 'tagArray',
                    required: true,
                    element: 'multiselect',
                    selectOptions: []
                },
                {
                    name: 'Add New Tag',
                    element: 'button',
                    class: 'itemModalButton',
                    onClick: function(){window.open('/tag', '_blank').focus();}
                },
                {
                    name: 'Refresh Tag List',
                    element: 'button',
                    class: 'itemModalButton',
                    onClick: this.getTags
                }
            ],
            expandedSections: {},
            allSectionsExpanded: false,
            ExpressionModalVisible: false,
            conditionalExpressionObject: null,
            ExpressionObjectType: ''
        };
    },
    computed: {
        SectionsThatCanBeLinked() {
            return this.accordion.sections
            .filter(s => 
                !s.linkedAccordionSectionID && 
                s.isActive && 
                s.headers?.length > 0
            )
            .map(s => ({
                internalName: s.id,
                displayName: `${s.sequence}. ${translation.getTextForLanguage(s.headers[0].minimizedTextJson, this.accordion.defaultLanguage) ||
                                    translation.getTextForLanguage(s.headers[0].expandedTextJson, this.accordion.defaultLanguage)}`
             }));
        },
        SectionsForPopup() {
            return [
                { internalName: 0, displayName: '' },
                ...this.SectionsThatCanBeLinked
            ]; 
        }
    },
    async mounted() {
        await this.getMediaProperties();
        await this.getSectionLayoutTypes();
        await this.getAccordion();
        await this.getMediaPropertyTypes();
        await this.getTags();
        this.surveys = await data.getAllSurveys('', true);
        this.surveys = sorting.sortArray(this.surveys, 'name', 'asc');
        this.testConfigurationFormRows[this.testConfigurationFormRows.findIndex(tc => tc.name === 'Survey')].selectOptions = this.surveys.map(sur => {return { displayName: sur.name, internalName: sur.id };});
        this.newSectionFormRows[1].selectOptions = this.SectionsForPopup;
    },
    methods: {
        sectionActions: function(sectionId) {
            if(!this.accordion || !this.accordion.actions)
                return [];
            if(sectionId > 0)
                return this.accordion.actions.filter(act => act.accordionSectionID === sectionId);
            else
                return this.accordion.actions.filter(act => !act.accordionSectionID)
        },
        addAction: function(action) {
            this.accordion.actions.push(action);
        },
        getPrintConditions: function(conditions) {
            if(conditions != '{}')
                return printConditions(JSON.parse(conditions));
            else 
                return '';
        },
        showConditionalModal: function(object, type) {
            this.conditionalExpressionObject = object;
            this.ExpressionModalVisible = true;
            this.ExpressionObjectType = type;
        },  
        updateConditions: function(conditionGroup) {
            this.conditionalExpressionObject.conditionalJson =  JSON.stringify(conditionGroup);
            switch(this.ExpressionObjectType) {
                case 'detail':
                    this.saveAccordionDetail(this.conditionalExpressionObject.accordionSectionID, this.conditionalExpressionObject, 'conditionalJson');
                    break;
                case 'header':
                    this.saveAccordionHeader(this.conditionalExpressionObject.accordionSectionId, this.conditionalExpressionObject, 'conditionalJson');
                    break;
                case 'section':
                    this.saveAccordionSection(this.conditionalExpressionObject, 'conditionalJson');
                    break;
                case 'extraText':
                    this.saveAccordionExtraText(this.conditionalExpressionObject, 'conditionalJson');
                    break;
                case 'action':
                    this.saveAccordionAction(this.conditionalExpressionObject, 'conditionalJson');
                    break;
            }
        },
        getAccordion: async function() {
            const urlParams = new URLSearchParams(window.location.search);
            this.accordion = await data.getAccordion(urlParams.get('ID'), null, !this.showDeletedElements);
            this.sortAccordion();
        },
        getSectionLayoutTypes: async function() {
            var lst = await data.getEnumList("AccordionSectionLayouts");
            this.layouts = sorting.sortArray(lst, "displayName", "asc", 0, 0);  
        },
        compareValues: function(key, order = 'asc') {
            return function innerSort(a, b) {
                const varA = (typeof a[key] === 'string')
                    ? a[key].toUpperCase() : a[key];
                const varB = (typeof b[key] === 'string')
                    ? b[key].toUpperCase() : b[key];

                let comparison = 0;
                if (varA > varB) {
                    comparison = 1;
                } 
                else if (varA < varB) {
                    comparison = -1;
                }

                return ( (order === 'desc') ? (comparison * -1) : comparison );
            };
        },
        getMediaProperties: async function() {
            this.mediaProperties = await data.getAllMediaProperties('', true);
        },
        getMediaPropertyTypes: async function() {
            this.mediaTypes = await data.getEnumList('MediaTypes');
        },
        getTags: async function() {
            let tags = await data.getTags('', 'AccordionDetail', true);
            tags.forEach(tag => {
                tag.value = tag.name;
                tag.displayName = tag.name;
            });
            this.tags = tags;
            this.sharedDetailFormRows.find(row => row.name === 'Tags').selectOptions = this.tags;
        },
        sortAccordion: function() {
            this.accordion.sections.sort((a,b) => {return a.sequence - b.sequence});
            this.accordion.headers.sort((a,b) => { return a.sequence - b.sequence});
            this.accordion.footers.sort((a,b) => { return a.sequence - b.sequence});
            this.accordion.sections.forEach(sec => {
                if(!sec.currentTab && sec.currentTab !== 0)
                    sec.currentTab = 0;
                sec.headers.sort((a,b) => {return a.sequence - b.sequence});
                sec.details.sort((a,b) => {return a.sequence - b.sequence});

                sec.details.forEach(detail => { 
                    if(detail.cssClass?.length) {
                        if(!Array.isArray(detail.cssClass))
                            detail.cssClass = detail.cssClass.split(' ');
                        detail.cssClass.forEach(cls => {
                            if(this.cssFieldElementsDetail.indexOf(cls) < 0)
                                this.cssFieldElementsDetail.push(cls);
                        });
                    }
                });
            });
            this.cssFieldElementsDetail.sort();
        },
        reactivateAccordion: async function() {
            this.accordion.isActive = true;
            this.processReactivateResult(await data.saveAccordion(this.accordion));
        },
        deactivateAccordion: async function() {
            if(confirm('Are you sure you want to deactivate this accordion?')) {
                this.accordion.isActive = false;
                this.processDeactivateResult(await data.saveAccordion(this.accordion));
            }
        },
        saveAccordion: async function(fieldName) {
            let obj = {};
            if(fieldName) {
                obj.id = this.accordion.id;
                obj[fieldName] = this.accordion[fieldName];
            }
            else 
                obj = this.accordion;

            this.processSaveResult(await data.saveAccordion(obj));
        },
        saveAccordionSection: async function(sec, fieldName) {

            let obj = {};
            if(fieldName) {
                obj.id = sec.id,
                obj[fieldName] = sec[fieldName]
            } else {
                obj = sec;
            }
            this.processSaveResult(await data.saveAccordionSection(this.accordion.id, obj));
            this.sortAccordion();
        },
        saveNewAccordionSection: async function(sec) {
            return this.processSaveResult(await data.saveAccordionSection(this.accordion.id, sec));
        },
        deleteAccordionSection: async function(accId, secId) {
            if(confirm('Are you sure you want to delete this section?')) {
                this.processDeactivateResult(await data.deleteAccordionSection(accId, secId));
                delete this.expandedSections[secId];
                this.getAccordion();
            }
        },
        reactivateAccordionSection: async function(accId, sec) {
            sec.isActive = true;
            this.processReactivateResult(await data.saveAccordionSection(accId, sec));
        },
        saveAccordionHeader: async function(secId, header, fieldName, showToaster = true) {
            let obj = {};
            if(fieldName) {
                obj.id = header.id;
                obj[fieldName] = header[fieldName];
                if(fieldName === 'minimizedText' || fieldName === 'expandedText') {
                    header[fieldName + 'Json'] = await translation.addTranslations(header[fieldName + 'Json'], header[fieldName], this.accordion.languages, this.accordion.defaultLanguage);
                    obj[fieldName + 'Json'] = header[fieldName + 'Json'];
                }
            } else {
                obj = header;
            }
            this.processSaveResult(await data.saveAccordionHeader(this.accordion.id, secId, obj), null, showToaster);
            this.sortAccordion();
        },
        saveNewAccordionHeader: async function(secId, header) {
            header.minimizedTextJson = await translation.addTranslations(header.minimizedTextJson, header.minimizedText, this.accordion.languages, this.accordion.defaultLanguage);
            header.expandedTextJson = await translation.addTranslations(header.expandedTextJson, header.expandedText, this.accordion.languages, this.accordion.defaultLanguage);
            return this.processSaveResult(await data.saveAccordionHeader(this.accordion.id, secId, header));
        },
        deleteAccordionHeader: async function(accId, secId, headId) {
            if(confirm('Are you sure you want to delete this header?')) {
                this.processDeactivateResult(await data.deleteAccordionHeader(accId, secId, headId));
                this.getAccordion();
            }
        },
        reactivateAccordionHeader: async function(accId, secId, head) {
            head.isActive = true;
            this.processReactivateResult(await data.saveAccordionHeader(accId, secId, head));
        },
        copyAccordion: async function() {
            this.processApiResult(await data.copyAccordion(this.accordion.id), null, true, 'Accordion Successfully Copied', 'Accordion Copy Failed');
        }, 
        showAddMediaPropertyModal: function(obj) {
            obj.showAddMediaPropertyModal = true;
        },
        hideAddMediaPropertyModal: function(obj) {
            obj.showAddMediaPropertyModal = false;
        },
        selectMediaProperty: function(sectionId, detail) {
            detail.mediaProperty = this.mediaProperties.find(mp => {return mp.id === detail.mediaPropertyID});
            this.saveAccordionDetail(sectionId, detail, 'mediaPropertyID');
        },
        removeMediaPropertyFromDetail: async function(det) {
            det.mediaPropertyID = 0;
            det.mediaProperty = null;
            await this.saveAccordionDetail(det.accordionSectionID, det, 'mediaPropertyID');
        },
        removeMediaPropertyFromExtraText: async function(ext) {
            ext.mediaPropertyID = 0;
            ext.mediaProperty = null;
            await this.saveAccordionExtraText(ext, 'mediaPropertyID');
        },
        saveAccordionDetail: async function(secId, det, fieldName, showToaster = true) {
            let obj = {};
            if(fieldName && det.id) {
                obj.id = det.id;
                switch(fieldName) {
                    case 'displayText':
                        det.displayTextJson = await translation.addTranslations(det.displayTextJson, det.displayText, this.accordion.languages, this.accordion.defaultLanguage);
                        obj.displayTextJson = det.displayTextJson;
                        obj.displayText = det.displayText;
                        break;
                    default:
                        obj[fieldName] = det[fieldName];
                        break;
                }
            } else {
                obj = det;
            }

            const resp = this.processSaveResult(await data.saveAccordionDetail(this.accordion.id, secId, obj), null, showToaster);
            if(!det.id) {
                if(det.mediaPropertyID)
                    this.accordion.sections.find(sec => sec.id === secId).details.find(detail => detail.mediaPropertyID === det.mediaPropertyID ).id = resp.id;
                else return this.getAccordion();
            }
            this.sortAccordion();
        },
        addAccordionDetail: async function(secId, det) {
            if(det.displayText) {
                det.displayTextJson = await translation.addTranslations(det.displayTextJson, det.displayText, this.accordion.languages, this.accordion.defaultLanguage);
                this.processSaveResult(await data.saveAccordionDetail(this.accordion.id, secId, det));
                this.getAccordion();

            } else {
                det.conditionalJson = '{}';
                det.accordionSectionID = secId;
                det.sharedDetailID = 0;
                this.accordion.sections.find(sec => sec.id === secId).details.push(det);
                this.sortAccordion();
            }
            return {success: true};
        },
        saveAccordionDetail_fromComponent: async function(detail, newValue, fieldName) {
            detail[fieldName] = newValue;
            this.saveAccordionDetail(detail.accordionSectionID, detail, fieldName);
        },
        deleteAccordionDetail: async function(accId, secId, detId) {
            if(confirm('Are you sure you want to delete this detail?')) {
                this.processDeactivateResult(await data.deleteAccordionDetail(accId, secId, detId));
                this.getAccordion();
            }
        },
        reactivateAccordionDetail: async function(accId, secId, det) {
            det.isActive = true;
            this.processReactivateResult(await data.saveAccordionDetail(accId, secId, det));
        },
        isSharedDetail: function(det) {
            return (det.sharedDetailID && det.sharedDetailID > 0);
        },
        saveNewSharedDetail: async function(sharedDet, det) {
            if(det.displayTextJson)
                sharedDet.displayTextJson = det.displayTextJson;
            if(det.mediaPropertyID && det.mediaPropertyID > 0)
                sharedDet.mediaPropertyID = det.mediaPropertyID;
            sharedDet.tags = sharedDet.tagArray.map(tag => tag.name);
            sharedDet.defaultLanguage = this.accordion.defaultLanguage;
            sharedDet.languages = this.accordion.languages;
            const result = this.processSaveResult(await data.saveSharedDetail(sharedDet));
            if(result.success) {
                det.sharedDetailID = result.id;
                det.mediaPropertyID = 0;
                det.displayText = '';
                this.processSaveResult(await data.saveAccordionDetail(this.accordion.id, det.accordionSectionID, det));
            }  
        },
        saveSharedDetail: async function(sharedDet, accDet) {
            accDet.sharedDetailID = sharedDet.id;
            const resp = this.processSaveResult(await data.saveAccordionDetail(this.accordion.id, accDet.accordionSectionID, accDet));
            if(!accDet.id)
                this.accordion.sections.find(sec => sec.id === accDet.accordionSectionID).details.find(detail => detail.sharedDetailID === accDet.sharedDetailID ).id = resp.id;
            sharedDet = await data.getSharedDetail(sharedDet.id);
            accDet.displayText = sharedDet.displayText;
            accDet.mediaPropertyID = sharedDet.mediaPropertyID;
            accDet.mediaProperty = sharedDet.mediaProperty;
            if(this.accordion.languages.length > 1) {
                for(let i = 0; i < this.accordion.languages.length; i++) {
                    if(sharedDet.languages.indexOf(this.accordion.languages[i]) < 0) {
                        sharedDet.displayTextJson.push(...await translation.getTranslations(sharedDet.displayText, this.accordion.languages[i], this.accordion.defaultLanguage));
                        sharedDet.languages.push(this.accordion.languages[i]);
                    }
                }
                this.processSaveResult(await data.saveSharedDetail(sharedDet));
            }
            accDet.languages = sharedDet.languages;
            accDet.displayTextJson = sharedDet.displayTextJson;
            accDet.showDisplayText = false;
        },
        saveAsUniqueDetail: async function(det) {
            det.sharedDetailID = 0;
            this.processSaveResult(await data.saveAccordionDetail(this.accordion.id, det.accordionSectionID, det));
        },
        saveNewAccordionExtraTextHeader: async function(extraText) {
            let obj = extraText;
            obj.ExtraTextLocation = "Header";
            obj.displayTextJson = await translation.addTranslations(obj.displayTextJson, obj.displayText, this.accordion.languages, this.accordion.defaultLanguage);
            return this.processSaveResult(await data.saveAccordionExtraText(this.accordion.id, obj));
        },
        saveNewAccordionExtraTextFooter: async function(extraText) {
            let obj = extraText;
            obj.ExtraTextLocation = "Footer";
            obj.displayTextJson = await translation.addTranslations(obj.displayTextJson, obj.displayText, this.accordion.languages, this.accordion.defaultLanguage);
            return this.processSaveResult(await data.saveAccordionExtraText(this.accordion.id, obj));
        },
        saveAccordionExtraText: async function(extraText, fieldName, showToaster = true) {
            let obj = {};
            if(fieldName) {
                obj.id = extraText.id;
                obj[fieldName] = extraText[fieldName];
                if(fieldName === 'displayText') {
                    extraText.displayTextJson = await translation.addTranslations(extraText.displayTextJson, extraText.displayText, this.accordion.languages, this.accordion.defaultLanguage);
                    obj.displayTextJson = extraText.displayTextJson;
                }
            } else {
                obj = extraText;
            }
            this.processSaveResult(await data.saveAccordionExtraText(this.accordion.id, obj), null, showToaster);
            this.sortAccordion();
        },
        saveAccordionExtraText_fromComponent: async function(extraText, newValue, fieldName) {
            extraText[fieldName] = newValue;
            this.saveAccordionExtraText(extraText, fieldName);
        },
        deleteAccordionExtraText: async function(accId, extraTextId, type) {
            if(confirm('Are you sure you want to delete this ' + type +'?')) {
                this.processDeactivateResult(await data.deleteAccordionExtraText(accId, extraTextId));
                this.getAccordion();
            }
        },
        reactivateAccordionExtraText: async function(accId, extraText) {
            extraText.isActive = true;
            this.processReactivateResult(await this.saveAccordionExtraText(extraText, "isActive"));
        },
        toggleOpen: function(sec) {
            if(!this.expandedSections[sec.id])
                this.expandedSections[sec.id] = false;
            else {
                sec.details.forEach(det => {
                    det.showConditionalJson = false;
                    det.showDisplayText = false;
                });
            }
                
            this.expandedSections[sec.id] = !this.expandedSections[sec.id];
        },
        toggleAllSections: function() {
            if(!this.allSectionsExpanded) {
                for(let i = 0; i < this.accordion.sections.length; i++) {
                    if(!this.accordion.sections[i].linkedAccordionSectionID)
                        this.expandedSections[this.accordion.sections[i].id] = true;
                }
                this.allSectionsExpanded = true;
            } else {
                const expandedSections = this.expandedSections;
                this.accordion.sections.forEach(sec => {
                    if(expandedSections[sec.id]) {
                        sec.details.forEach(det => {
                            det.showConditionalJson = false;
                            det.showDisplayText = false;
                        })
                    }
                })
                this.expandedSections = {};
                this.allSectionsExpanded = false;
            }
        },
        goToList: function() {
            window.location.href = '/accordions';
        },
        activeItems: function(itemArr) {
            if(itemArr)
                return itemArr.filter(i => this.showDeletedElements || i.isActive);
        },
        saveMediaPropertyToDetail: async function(det, mp) {
            if(mp.mediaType === 'ToolTip' && det.displayText.length === 0) {
                this.$toast.error('ToolTip media must have Display Text');
                return;
            }
            det.mediaPropertyID = mp.id;
            det.mediaProperty = await data.getMediaProperty(mp.id);
            if(this.accordion.languages.length > 1) {
                this.accordion.languages.forEach(async lang => {
                    if(lang === 'English') return;
                    this.selectedLanguage = lang;
                    await this.addLanguageToMediaProperty(det.mediaProperty);
                    this.selectedLanguage = null;
                });
            }
            await this.saveAccordionDetail(det.accordionSectionID, det, 'mediaPropertyID');
        },
        saveMediaPropertyToExtraText: function(ext, mp) {
            ext.mediaPropertyID = mp.id;
            ext.mediaProperty = mp;
            this.saveAccordionExtraText(ext, 'mediaPropertyID');
        },
        saveTestConfiguration: async function(tc) {
            tc.accordionID = this.accordion.id;
            const result = this.processSaveResult(await data.saveTestConfiguration(tc));
            window.location.href = '/testconfiguration?id=' + result.id;
        },
        goToTestConfiguration: function() {
            window.location.href = '/testconfiguration?id=' + this.accordion.testConfigurationId;
        },
        updateCssFieldExtraText: async function(extText, newValue) {
            extText.cssClass = newValue;
        },
        onCssFieldAutoCompleteBlurExtraText: async function(extText, newValue) {
            extText.cssClass = newValue;
            await this.saveAccordionExtraText(extText, 'cssClass');
        },
        addCssClass: function(newClass, detail, sectionId) {
            this.cssFieldElementsDetail.push(newClass);
            if(!Array.isArray(detail.cssClass))
                detail.cssClass = [];
            detail.cssClass.push(newClass);
            this.saveAccordionDetail(sectionId, detail, 'cssClass');
        },
        isAudio: function(action) {
            return (action.type === 'PlayAudio');
        },
        toggleConfiguration: function(action) {
            if(action.showConfiguration)
                action.showConfiguration = false;
            else
                action.showConfiguration = true;
        },
        saveAccordionAction: async function(act, fieldName) {
            let obj = {};
            if(fieldName) {
                obj.id = act.id;
                obj[fieldName] = act[fieldName];
            } else {
                obj = act;
            }
            this.processSaveResult(await data.saveAccordionAction(obj, this.accordion.id));
        },
        addLanguage: async function(language) {
            this.accordion.languages.push(language);
            for(let i = 0; i < this.accordion.sections.length; i++) {
                for(let j = 0; j < this.accordion.sections[i].headers.length; j++) {
                    if(this.accordion.sections[i].headers[j].minimizedText) {
                        await translation.addTranslationToField(this.accordion.sections[i].headers[j].minimizedTextJson, language, this.accordion.defaultLanguage, true);
                        await this.saveAccordionHeader(this.accordion.sections[i].id, this.accordion.sections[i].headers[j], 'minimizedTextJson', false);
                    }
                    if(this.accordion.sections[i].headers[j].expandedText) {
                        await translation.addTranslationToField(this.accordion.sections[i].headers[j].expandedTextJson, language, this.accordion.defaultLanguage, true);
                        await this.saveAccordionHeader(this.accordion.sections[i].id, this.accordion.sections[i].headers[j], 'expandedTextJson', false);
                    }
                }
                for(let j = 0; j < this.accordion.sections[i].details.length; j++) {
                    if(!this.accordion.sections[i].details[j].sharedDetailID) {
                        if(this.accordion.sections[i].details[j].displayText) {
                            await translation.addTranslationToField(this.accordion.sections[i].details[j].displayTextJson, language, this.accordion.defaultLanguage, true);
                            await this.saveAccordionDetail(this.accordion.sections[i].id, this.accordion.sections[i].details[j], 'displayTextJson', false);
                        }
                        if(this.accordion.sections[i].details[j].mediaProperty)
                            await this.addLanguageToMediaProperty(this.accordion.sections[i].details[j].mediaProperty, language);
                    } else {
                        let sharedDetail = await data.getSharedDetail(this.accordion.sections[i].details[j].sharedDetailID);
                        if(sharedDetail.languages.indexOf(language) < 0) {
                            sharedDetail.languages.push(language);
                            await translation.addTranslationToField(sharedDetail.displayTextJson, language, this.accordion.defaultLanguage, true);
                            await data.saveSharedDetail(sharedDetail);
                            this.accordion.sections[i].details[j].displayTextJson = sharedDetail.displayTextJson;
                        }
                    }
                }
            }
            for(let i = 0; i < this.accordion.headers.length; i++) {
                if(this.accordion.headers[i].displayText) {
                    await translation.addTranslationToField(this.accordion.headers[i].displayTextJson, language, this.accordion.defaultLanguage, true);
                    await this.saveAccordionExtraText(this.accordion.headers[i], 'displayTextJson', false);
                    if(this.accordion.headers[i].mediaProperty)
                        await this.addLanguageToMediaProperty(this.accordion.headers[i].mediaProperty, language);
                }
            }
            for(let i = 0; i < this.accordion.footers.length; i++) {
                if(this.accordion.footers[i].displayText) {
                    await translation.addTranslationToField(this.accordion.footers[i].displayTextJson, language, this.accordion.defaultLanguage, true);
                    await this.saveAccordionExtraText(this.accordion.footers[i], 'displayTextJson', false);
                    if(this.accordion.footers[i].mediaProperty)
                        await this.addLanguageToMediaProperty(this.accordion.footers[i].mediaProperty, language);
                }
            }
            await this.saveAccordion('languages');
        },
        addLanguageToMediaProperty: async function(mp, language) {
            // Don't translate if already translated
            if(mp.languages.indexOf(language) >= 0) return;

            switch(mp.mediaType) {
                case 'SampleLanguage':
                    await translation.addTranslationToField(mp.configuration.textJson, language, mp.defaultLanguage, true);
                    await translation.addTranslationToField(mp.configuration.copyButtonTextJson, language, mp.defaultLanguage, true);
                    break;
                case 'ToolTip':
                    await translation.addTranslationToField(mp.configuration.textJson, language, mp.defaultLanguage, true);
                    break;
                case 'Table':
                    mp.configuration.rows.forEach(async row => {
                        row.cells.forEach(async cell => {
                            if(cell.displayText)
                                await translation.addTranslationToField(cell.displayTextJson, language, mp.defaultLanguage, true);
                        })
                    })
                    break;
                default:
                    // Don't translate or save other Media Properties
                    return;
            }
            mp.languages.push(this.selectedLanguage);
            this.processSaveResult(await data.saveMediaProperty({
                id: mp.id,
                configuration: mp.configuration,
                languages: mp.languages
            }), null, false);
        },
        deleteLanguage: async function(lang) {
            const ind = this.accordion.languages.indexOf(lang)
            this.accordion.languages.splice(ind, 1);
            await this.saveAccordion('languages');
        },
        changeDefaultLanguage: async function(lang) {
            this.accordion.defaultLanguage = lang;
            await this.saveAccordion('defaultLanguage');
        },
        getDetailDisplayText: function(detail) {
            return detail.displayText || translation.getTranslatedText(this.accordion.defaultLanguage, detail.displayTextJson);
        },
        generalFormRows: function(displayTextRequiredBool) {
            return [
                {
                    name: 'Sequence',
                    propertyName: 'sequence',
                    required: true,
                    element: 'input'
                },
                {
                    name: 'Display Text',
                    propertyName: 'displayText',
                    required: displayTextRequiredBool,
                    element: 'textarea'
                }
            ]
        }
    }
}
</script>

<style scoped>
    .accordion-info-block {
        display: flex;
        flex-direction: column;
    }
    .languages-container {
        display:flex;
        flex-direction:row;
        justify-content:space-between;
        align-items:center;
        width: 100%;
    }
    .addLanguageButton {
        height: 30px;
        border-radius: 5px;
        color: white;
        background-color: #036991;
        border-color: #036991;
        font-size: 12px;
        cursor: pointer;
        margin-bottom: 10px;
    }
    .removeLanguageButton {
        display: inline-block;
        background-color: unset;
        border: none;
        cursor: pointer;
        font-size: 12px;
        color:darkred;
    }
    .accordionButtons {
        display: flex;
        flex-direction: column;
        align-items: center;
    }
    :deep(.translationButton), :deep(.testButton) {
        height: 40px;
        width: 200px;
        font-size: 16px;
        background-color: lightgray;
        border-color: lightgray;
        color: black;
        border-radius: 5px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        margin-left: -15px;
        margin-bottom: 10px;
    }
 
    .section .reactivateButton {
        position: absolute;
        top: 0px;
        left: 20px;
    }
    .sectionHeader .reactivateButton, .detail .reactivateButton {
        position: absolute;
        top: 20px;
        right: 20px;
    }
    .deactivateButton {
        background-color: darkred;
        border-color: darkred;
        color: white;
    }

    .reactivateButton {
        height: 40px;
        width: 200px;
        border-radius: 5px;
        background-color: lightgreen;
        border-color: lightgreen;
    }

    .testButton {
        background-color: lightgray;
        border-color: lightgray;
        color: black;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: 0px;
    }

    .deactivateButton, .reactivateButton, .testButton {
        height: 40px;
        width: 200px;
        border-radius: 5px;
        font-size: 16px;
        cursor: pointer;
        margin-bottom: 10px;        
    }

    .sectionContainer {
        text-align: left;
        min-height: 200px;
        width: 97%;
        padding: 20px;
        border: 1px solid #f0f0f0;
        background-color: #f0f0f0;
        border-radius: 20px;
        margin-bottom: 20px;
        display: flex;
        flex-wrap: wrap;
    }
    .sectionMinimized {
        display: flex;
        flex: 1 1 100%;
        align-items: center;
        margin-bottom: 20px;
        position: relative;
        align-self: stretch;
    }
    .deleteButton {
        position: absolute;
        width: 40px;
        height: 40px;
        background-color: darkred;
        border-color: darkred;
        color: white;
        border-radius: 10px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
    }
    .deleteButton:disabled {
        color: gray;
    }
    .ButtonAbsolute {
        position: absolute;
        top: 15px;
        right: 15px;
    }
    .section .deleteButton {
        top: 0px;
        left: 10px;
    }
    .sectionHeader .deleteButton, .detail .deleteButton {
        top: 20px;
        right: 20px;
    }
    .section {
        display: flex;
        flex-wrap: wrap;
        position: relative;
        flex: 0 0 260px;
        align-self: stretch;
        align-items: center;
        padding: 40px 0 0 20px;
    }
    .sectionTitle {
        flex: 1 0 120px;
    }
    .sectionJson {
        flex: 0 0 250px;
        margin-bottom: 20px;
    }
    .sectionJson textarea {
        width: 100%;
        min-height: 80px;
    }
    .sectionTitle span {
        cursor: pointer;
    }
    .sectionSequence {
        padding-left: 20px;
        flex: 0 0 80px;
    }
    .sectionSequence input {
        width: 50px;
    }
    .sectionHeader {
        flex: 1 1 700px;
        border: 1px solid lightgray;
        border-radius: 20px;
        background-color: lightgray;
        padding: 20px;
        margin: 0 10px;
        position: relative;
    }
    .sectionHeader input {
        width: 100%;
    }
    .headerFieldContainer {
        display: flex;
        justify-content: center;
        width: 100%;
    }
    .headerSequence input {
        width: 50px;
    }
    .headerTextFields {
        margin-right: 40px;
    }
    .headerTextFields, .headerConditionalJson {
        flex: 1 1 500px;
    }
    :deep( .conditionalJson) {
        padding-right: 20px;
    }
    :deep(.modal .conditionalJson) {
        padding-right: 0;
    }
    :deep(.sectionContainer .conditionalJson textarea) {
        width: 100%;
        min-height: 100px;
    }
    .showHideDetails, .showHideAll {
        cursor: pointer;
    }
    .sectionExpanded {
        margin: 0 10px 0 280px;
        flex: 1 1 100%;
    }
    .detail {
        position: relative;
        display: flex;
        flex-wrap: wrap;
        border-top: 1px solid lightgray;
        margin: 10px 0;
        border: 1px solid lightgray;
        border-radius: 20px;
        background-color: lightgray;
        padding: 13px;
    }
    .detailSequence1 {
        flex: 0 0 100px;
    }
    .detailSequence input {
        width: 50px;
    }
    .detailCssClass {
        flex: 0 1 200px;
        padding: 0 20px 0 10px;
    }
    .detailCssClass input {
        width: 100%;
    }
    .detailConditionalJson {
        padding: 0 20px 0 0;
        flex: 1 1 300px;
    }

    :deep(.conditionalJsonText div) {
        display: inline;
    }
    .detailMediaProperty {
        flex: 1 1 300px;
        padding: 0 10px;
        margin-left: 20px;
        margin-top: 10px;
    }
    .openModalButton {
        display: inline-block;
        background-color: unset;
        border: none;
        cursor: pointer;
        font-size: 12px;
        color:#036991;
    }
    .editFieldButton {
        display: inline-block;
        background-color: unset;
        border: none;
        cursor: pointer;
        color:#036991;
        margin-left: 10px;
    }
    .removeMediaPropertyButton {
        display: inline-block;
        background-color: unset;
        border: none;
        cursor: pointer;
        font-size: 12px;
        color:darkred;
    }
    .detailMediaProperty span {
        display: inline-block;
        margin: 16px 20px 16px 0;
    }
    .detailDisplayText {
        flex: 1 1 700px;
        padding: 0 20px;
    }
    .detailConditionalJson textarea, .detailDisplayText textarea {
        width: 100%;
        min-height: 100px;
    }

    
    .conditionalJsonButtonContainer button {
        margin: 0 20px 10px 0;
        padding: 1px 3px;
        background-color: #FFD831;
        border: 1px solid #000000;
        border-radius: 5px;
        cursor: pointer;
        font-size: 16px;
    }

    .flexRow {
        display: flex; 
        flex-direction: row; 
        width: 100%;
    }

    .replacementStringContainer {
        margin: auto 0px; 
        padding-left: 30px;
    }

    .detailHeader {
        font-weight: bold;
        width: 120px;
        text-align: left;
    }

    :deep(.itemModalButton) {
        margin-left: 300px;
        background-color: #036991;
        color: white;
        height: 40px;
        width: 200px;
        border-radius: 6px;
        border-color: #036991;
        cursor: pointer;
        font-size: 16px;
    }
    :deep(.sharedDetailButton) {
        background-color: #036991;
        color: white;
        height: 40px;
        width: 200px;
        border-radius: 6px;
        border-color: #036991;
        cursor: pointer;
        font-size: 16px;
    }

    .flexButtonContainer {
        width: 80%;
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: space-between;
    }
    :deep(.autoCompleteContainer) {
        font-size: medium;
    }
    :deep(.autoCompleteContainer .dropdown-menu) {
        font-size:medium;
    }
    .tabsContainer{
        display: flex;
        width: 100%;
        margin: 0 84px 0 310px;
        border-bottom: solid 1px gray;
    }
    .tab {
        flex: 0 1 150px;
        font-size: 20px;
        border-top: solid 1px gray;
        border-left: solid 1px gray;
        border-right: solid 1px gray;
        margin-right: 10px;
        padding: 10px 0;
        text-align: center;
        background-color: lightgray;
        border-top-left-radius: 20px;
        border-top-right-radius: 20px;
        cursor: pointer;
    }
    .tab.active {
        background-color: gray;
        color: white;
        cursor: default;
    }
    :deep(.tooltip-content-container) {
        max-width: 20em;
        background: white;
        border: 1px solid black;
        border-radius: 15px;
        padding: 10px;
    }
    .notSaved {
        color: red;
    }
    .detailDisplayText :deep(> p > b)::before {
        content: '*';
    }
</style>